import { useState, ChangeEvent, FormEvent } from "react";

// --- Interfaces ---
interface FormDataState {
  // 1. Dados de Pagamento
  nomeCobranca: string;
  cpfCnpj: string;
  telefoneCobranca: string;
  diaCobranca: string;

  // 2. Dados do Negócio
  nomeEmpresa: string;
  nicho: string;
  historiaEmpresa: string;

  // 3. Área de Atendimento
  regiaoAtendimento: string;
  pretendeExpandir: string;
  regiaoExpansao: string;

  // 4. Oferta Principal
  servicoPrincipal: string;

  // 5. Cliente Ideal
  tipoCliente: string;
  faixaEtaria: string;
  comportamentoCliente: string;

  // 6. Diferenciais
  diferenciais: string;

  // 7. Concorrência
  concorrentes: string;

  // 8. Histórico
  jaAnunciou: string;
  motivoFalha: string;
  palavrasChaveGoogle: string;

  // 9. Atendimento & Comercial
  crm: string;
  notaComercial: string;

  // 10. Faturamento
  notaFaturamento: string;
  metaFaturamento: string;

  // 11. Alinhamento Final
  problemaMagico: string;
  expectativaTrafego: string;
  observacoes: string;
}

// --- Funções Auxiliares de Formatação ---

const applyCpfCnpjMask = (value: string) => {
  const digits = value.replace(/\D/g, "");

  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const applyPhoneMask = (value: string) => {
  const digits = value.replace(/\D/g, "");

  // Limita a 11 dígitos (DDD + 9 números)
  const limitedDigits = digits.slice(0, 11);

  if (limitedDigits.length <= 10) {
    // Formato Fixo: (00) 0000-0000
    return limitedDigits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  // Formato Celular: (00) 00000-0000
  return limitedDigits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

// --- Ícones SVG ---
const IconFlame = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#f97316"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);
const IconCheck = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#22c55e"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const IconArrowRight = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);
const IconChevronDown = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// --- Componentes Auxiliares ---

const SectionHeader = ({
  number,
  title,
}: {
  number: string;
  title: string;
}) => (
  <div className="mt-32 mb-6 first:mt-2">
    <h3 className="text-xl font-black text-white uppercase tracking-wider flex items-center gap-3">
      <span className="bg-orange-600 text-white text-sm w-8 h-8 flex items-center justify-center rounded-lg shadow-lg shadow-orange-900/50">
        {number}
      </span>
      {title}
    </h3>
    <div className="h-[1px] w-full bg-slate-800 mt-4 ml-2"></div>
  </div>
);

const SelectionGroup = ({
  options,
  value,
  onChange,
  cols = 2,
}: {
  options: string[];
  value: string;
  onChange: (val: string) => void;
  cols?: number;
}) => {
  const gridClass = cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return (
    <div className={`grid grid-cols-1 ${gridClass} gap-3 mt-2`}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`py-3 px-4 rounded-xl text-sm font-bold text-left transition-all duration-200 border border-slate-700/50 ${
            value === opt
              ? "bg-orange-600/20 border-orange-500 text-white shadow-[0_0_10px_rgba(249,115,22,0.3)]"
              : "bg-slate-800/40 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

const InputLabel = ({
  label,
  className = "",
}: {
  label: string;
  className?: string;
}) => (
  <label
    className={`block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 ${className}`}
  >
    {label}
  </label>
);

const RatingScale = ({
  value,
  onChange,
  label,
  className = "mt-4",
}: {
  value: string;
  onChange: (val: string) => void;
  label: string;
  className?: string;
}) => (
  <div className={className}>
    <InputLabel label={label} />
    <div className="flex justify-between gap-1 mt-2 overflow-x-auto pb-2">
      {Array.from({ length: 11 }, (_, i) => String(i)).map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => onChange(num)}
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all border border-slate-700/50 flex-shrink-0 ${
            value === num
              ? "bg-orange-600 text-white border-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.4)] scale-110"
              : "bg-slate-800/40 text-slate-400 hover:bg-slate-700 hover:text-white"
          }`}
        >
          {num}
        </button>
      ))}
    </div>
    <div className="flex justify-between text-[10px] text-slate-500 uppercase font-bold tracking-wider px-1">
      <span>Ruim</span>
      <span>Excelente</span>
    </div>
  </div>
);

const StyledInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 shadow-inner text-sm"
  />
);

// --- Componente Principal ---
export default function App() {
  const [step, setStep] = useState<"form" | "success">("form");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para controlar os dropdowns
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isAgeDropdownOpen, setIsAgeDropdownOpen] = useState(false);

  const [formData, setFormData] = useState<FormDataState>({
    nomeCobranca: "",
    cpfCnpj: "",
    telefoneCobranca: "",
    diaCobranca: "",

    nomeEmpresa: "",
    nicho: "",
    historiaEmpresa: "",

    regiaoAtendimento: "",
    pretendeExpandir: "",
    regiaoExpansao: "",

    servicoPrincipal: "",

    tipoCliente: "",
    faixaEtaria: "",
    comportamentoCliente: "",

    diferenciais: "",
    concorrentes: "",

    jaAnunciou: "",
    motivoFalha: "",
    palavrasChaveGoogle: "",

    crm: "",
    notaComercial: "",

    notaFaturamento: "",
    metaFaturamento: "",

    problemaMagico: "",
    expectativaTrafego: "",
    observacoes: "",
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Aplica a máscara se o campo for CPF/CNPJ
    if (name === "cpfCnpj") {
      finalValue = applyCpfCnpjMask(value);
    }

    // Aplica a máscara se o campo for telefoneCobranca
    if (name === "telefoneCobranca") {
      finalValue = applyPhoneMask(value);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSelection = (name: keyof FormDataState, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch("https://formsubmit.co/ajax/tiagobandeiira@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _subject: `🔥 Briefing: ${formData.nomeEmpresa}`,
          _template: "table",
          _captcha: "false",
          ...formData,
        }),
      });
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      setStep("success");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-orange-600 opacity-10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-red-600 opacity-5 rounded-full blur-[120px]"></div>
      </div>

      <div className="w-full max-w-3xl bg-slate-900/80 backdrop-blur-xl border border-white/5 rounded-3xl shadow-2xl overflow-hidden relative z-10">
        <div className="h-1.5 w-full bg-gradient-to-r from-orange-600 via-red-600 to-orange-500"></div>

        <div className="p-6 md:p-12">
          <div className="flex flex-col items-center text-center mb-10">
            <div className="bg-slate-800 p-4 rounded-full mb-4 border border-orange-500/20 shadow-xl">
              <IconFlame />
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-1 uppercase">
              BRIEFING
            </h1>
            <p className="text-orange-500 font-bold tracking-[0.2em] text-[10px] uppercase">
              ONFIRE Assessoria em Tráfego Pago
            </p>
            <p className="text-slate-400 text-sm mt-4 max-w-lg mx-auto italic leading-relaxed">
              "Algumas perguntas rápidas e necessárias para que possamos
              desenvolver o melhor trabalho possível e alcançar resultados
              expressivos."
            </p>
          </div>

          {step === "form" ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* 1. DADOS DE PAGAMENTO */}
              <SectionHeader number="1" title="Dados de Pagamento" />
              <p className="text-sm text-slate-400 mb-4">
                Preencha por favor os dados abaixo para cadastrarmos no sistema
                de cobranças Asaas (nosso sistema automático de cobrança).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputLabel
                    label="Nome para cobrança (pessoal ou empresa)"
                    className="min-h-[2.5rem] flex items-end"
                  />
                  <StyledInput
                    required
                    name="nomeCobranca"
                    value={formData.nomeCobranca}
                    onChange={handleChange}
                    placeholder="Nome completo ou Razão Social"
                  />
                </div>
                <div>
                  <InputLabel
                    label="CPF/CNPJ"
                    className="min-h-[2.5rem] flex items-end"
                  />
                  <StyledInput
                    required
                    name="cpfCnpj"
                    value={formData.cpfCnpj}
                    onChange={handleChange}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputLabel
                    label="Telefone para contato (Financeiro)"
                    className="min-h-[2.5rem] flex items-end"
                  />
                  <StyledInput
                    required
                    type="tel"
                    name="telefoneCobranca"
                    value={formData.telefoneCobranca}
                    onChange={handleChange}
                    placeholder="(00) 0000-0000"
                  />
                </div>

                {/* DROP DOWN: DIA DA COBRANÇA */}
                <div className="relative">
                  <InputLabel
                    label="Dia da cobrança (mais próximo do start das campanhas)"
                    className="min-h-[2.5rem] flex items-end"
                  />
                  <button
                    type="button"
                    onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                    className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 shadow-inner text-sm flex justify-between items-center text-left"
                  >
                    <span
                      className={
                        formData.diaCobranca ? "text-white" : "text-slate-500"
                      }
                    >
                      {formData.diaCobranca || "Selecione o dia"}
                    </span>
                    <div
                      className={`transition-transform duration-200 ${
                        isDateDropdownOpen ? "rotate-180" : ""
                      }`}
                    >
                      <IconChevronDown />
                    </div>
                  </button>

                  {isDateDropdownOpen && (
                    <div className="absolute z-20 top-[calc(100%+4px)] left-0 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                      {["Dia 05", "Dia 15", "Dia 25"].map((dia) => (
                        <button
                          key={dia}
                          type="button"
                          onClick={() => {
                            handleSelection("diaCobranca", dia);
                            setIsDateDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm font-bold transition-colors border-b border-slate-800 last:border-0 ${
                            formData.diaCobranca === dia
                              ? "bg-orange-600 text-white"
                              : "text-slate-400 hover:bg-slate-800 hover:text-white"
                          }`}
                        >
                          {dia}
                        </button>
                      ))}
                    </div>
                  )}
                  {isDateDropdownOpen && (
                    <div
                      className="fixed inset-0 z-10 cursor-default"
                      onClick={() => setIsDateDropdownOpen(false)}
                    ></div>
                  )}
                </div>
              </div>

              {/* 2. DADOS DO NEGÓCIO */}
              <SectionHeader number="2" title="Dados do Negócio" />
              <div>
                <InputLabel label="Nome da Empresa" />
                <StyledInput
                  required
                  name="nomeEmpresa"
                  value={formData.nomeEmpresa}
                  onChange={handleChange}
                  placeholder="Ex: Barbearia Viking, Clínica Vida Plena"
                />
              </div>

              <div>
                <InputLabel label="Nicho de Atuação" />
                <StyledInput
                  name="nicho"
                  value={formData.nicho}
                  onChange={handleChange}
                  placeholder="Ex: Estética, Odontologia, Varejo..."
                />
              </div>

              <div>
                <InputLabel label="Como a empresa começou?" />
                <p className="text-[10px] text-slate-500 mb-2">
                  Fique à vontade para falar o quanto quiser.
                </p>
                <textarea
                  name="historiaEmpresa"
                  value={formData.historiaEmpresa}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-28 resize-none shadow-inner text-sm"
                  placeholder="Conte um pouco da sua história..."
                ></textarea>
              </div>

              {/* 3. ÁREA DE ATENDIMENTO */}
              <SectionHeader number="3" title="Área de Atendimento" />
              <div>
                <InputLabel label="Em qual região você atende hoje?" />
                <textarea
                  required
                  name="regiaoAtendimento"
                  value={formData.regiaoAtendimento}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-20 resize-none shadow-inner text-sm"
                  placeholder="Ex: Zona Sul do Rio de Janeiro, Bairros X, Y e Z, Toda a cidade..."
                ></textarea>
              </div>

              <div className="mt-4">
                <InputLabel label="Você pretende expandir para mais lugares ou para uma região maior?" />
                <SelectionGroup
                  options={["Sim", "Não"]}
                  value={formData.pretendeExpandir}
                  onChange={(val) => handleSelection("pretendeExpandir", val)}
                />
              </div>

              {formData.pretendeExpandir === "Sim" && (
                <div className="mt-3">
                  <InputLabel label="Para onde?" />
                  <StyledInput
                    name="regiaoExpansao"
                    value={formData.regiaoExpansao}
                    onChange={handleChange}
                    placeholder="Ex: Cidades vizinhas, todo o Estado, todo o País..."
                  />
                </div>
              )}

              {/* 4. OFERTA PRINCIPAL */}
              <SectionHeader number="4" title="Oferta Principal" />
              <div>
                <InputLabel label="Qual produto/serviço você vende?" />
                <p className="text-[10px] text-slate-500 mb-2">
                  Se for mais de um, liste eles por favor.
                </p>
                <textarea
                  required
                  name="servicoPrincipal"
                  value={formData.servicoPrincipal}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-24 resize-none shadow-inner text-sm"
                  placeholder="Ex: Limpeza de Pele, Combo de Lanches..."
                ></textarea>
              </div>

              {/* 5. CLIENTE IDEAL */}
              <SectionHeader number="5" title="Cliente Ideal" />
              <InputLabel label="Seu cliente é:" />
              <SelectionGroup
                options={["Pessoa física", "Empresas", "Ambos"]}
                value={formData.tipoCliente}
                onChange={(val) => handleSelection("tipoCliente", val)}
                cols={3}
              />

              {/* DROP DOWN: FAIXA ETÁRIA */}
              <div className="relative mt-4">
                <InputLabel label="Faixa etária mais comum:" />
                <button
                  type="button"
                  onClick={() => setIsAgeDropdownOpen(!isAgeDropdownOpen)}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 shadow-inner text-sm flex justify-between items-center text-left"
                >
                  <span
                    className={
                      formData.faixaEtaria ? "text-white" : "text-slate-500"
                    }
                  >
                    {formData.faixaEtaria || "Selecione a faixa etária"}
                  </span>
                  <div
                    className={`transition-transform duration-200 ${
                      isAgeDropdownOpen ? "rotate-180" : ""
                    }`}
                  >
                    <IconChevronDown />
                  </div>
                </button>

                {isAgeDropdownOpen && (
                  <div className="absolute z-20 top-[calc(100%+4px)] left-0 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {["18–24", "25–34", "35–44", "45–54", "55+"].map(
                      (faixa) => (
                        <button
                          key={faixa}
                          type="button"
                          onClick={() => {
                            handleSelection("faixaEtaria", faixa);
                            setIsAgeDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm font-bold transition-colors border-b border-slate-800 last:border-0 ${
                            formData.faixaEtaria === faixa
                              ? "bg-orange-600 text-white"
                              : "text-slate-400 hover:bg-slate-800 hover:text-white"
                          }`}
                        >
                          {faixa}
                        </button>
                      )
                    )}
                  </div>
                )}
                {isAgeDropdownOpen && (
                  <div
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setIsAgeDropdownOpen(false)}
                  ></div>
                )}
              </div>

              <InputLabel label="Esse cliente geralmente:" />
              <SelectionGroup
                options={[
                  "Já procura esse produto/serviço",
                  "Precisa ser convencido",
                ]}
                value={formData.comportamentoCliente}
                onChange={(val) => handleSelection("comportamentoCliente", val)}
              />

              {/* 6. DIFERENCIAIS */}
              <SectionHeader number="6" title="Diferenciais" />
              <div>
                <InputLabel label="Liste os diferenciais da sua empresa:" />
                <p className="text-[10px] text-slate-500 mb-2">
                  Quais são seus pontos fortes? Atendimento, preço, qualidade,
                  etc.
                </p>
                <textarea
                  name="diferenciais"
                  value={formData.diferenciais}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-24 resize-none shadow-inner text-sm"
                  placeholder="Ex: Atendimento 24h, garantia estendida, equipe certificada..."
                ></textarea>
              </div>

              {/* 7. CONCORRÊNCIA */}
              <SectionHeader number="7" title="Concorrência" />
              <div>
                <InputLabel label="Liste seus principais concorrentes:" />
                <textarea
                  name="concorrentes"
                  value={formData.concorrentes}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-20 resize-none shadow-inner text-sm"
                  placeholder="Ex: Empresa X, Empresa Y..."
                ></textarea>
              </div>

              {/* 8. HISTÓRICO DE ANÚNCIOS */}
              <SectionHeader number="8" title="Histórico" />
              <InputLabel label="Já anunciou antes?" />
              <SelectionGroup
                options={["Sim", "Não"]}
                value={formData.jaAnunciou}
                onChange={(val) => handleSelection("jaAnunciou", val)}
              />
              {formData.jaAnunciou === "Sim" && (
                <div className="mt-3">
                  <InputLabel label="Conte como foi a sua experiência:" />
                  <p className="text-[10px] text-slate-500 mb-2">
                    Ex: Já fiz por conta própria, anunciei e não tive
                    resultado...
                  </p>
                  <textarea
                    name="motivoFalha"
                    value={formData.motivoFalha}
                    onChange={handleChange}
                    className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-24 resize-none shadow-inner text-sm"
                    placeholder="Descreva aqui..."
                  ></textarea>
                </div>
              )}

              <div>
                <InputLabel label="No Google, como seus clientes costumam pesquisar pelo seu serviço?" />
                <StyledInput
                  name="palavrasChaveGoogle"
                  value={formData.palavrasChaveGoogle}
                  onChange={handleChange}
                  placeholder="Ex: 'dentista perto de mim'..."
                />
              </div>

              {/* 9. ATENDIMENTO E COMERCIAL */}
              <SectionHeader number="9" title="Atendimento & Comercial" />

              <div className="mt-2">
                <InputLabel label="Hoje você utiliza algum CRM? Se sim, qual?" />
                <StyledInput
                  name="crm"
                  value={formData.crm}
                  onChange={handleChange}
                  placeholder="Ex: RD Station, Kommo, Planilha..."
                />
              </div>

              <RatingScale
                className="mt-12"
                label="De 0 a 10, como você avalia seu comercial hoje?"
                value={formData.notaComercial}
                onChange={(val) => handleSelection("notaComercial", val)}
              />

              {/* 10. FATURAMENTO */}
              <SectionHeader number="10" title="Faturamento" />

              <div className="mt-2">
                <InputLabel label="Qual nosso principal objetivo ou meta de faturamento?" />
                <StyledInput
                  name="metaFaturamento"
                  value={formData.metaFaturamento}
                  onChange={handleChange}
                  placeholder="Ex: Faturar R$ 50k/mês"
                />
              </div>

              <RatingScale
                className="mt-12"
                label="De 0 a 10, quanto você está satisfeito(a) com seu faturamento hoje?"
                value={formData.notaFaturamento}
                onChange={(val) => handleSelection("notaFaturamento", val)}
              />

              {/* 11. ALINHAMENTO FINAL */}
              <SectionHeader number="11" title="Alinhamento Final" />

              <div>
                <InputLabel label="Se você pudesse resolver qualquer problema da empresa com um estalar de dedos, qual seria?" />
                <textarea
                  name="problemaMagico"
                  value={formData.problemaMagico}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-20 resize-none shadow-inner text-sm"
                ></textarea>
              </div>

              <div>
                <InputLabel label="Qual a principal expectativa quanto ao tráfego pago? Sinceridade, por favor 🫶" />
                <textarea
                  name="expectativaTrafego"
                  value={formData.expectativaTrafego}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-20 resize-none shadow-inner text-sm"
                ></textarea>
              </div>

              <div>
                <InputLabel label="Espaço livre para abrir o coração ou deixar qualquer consideração que tenha faltado:" />
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all placeholder:text-slate-600 h-24 resize-none shadow-inner text-sm"
                ></textarea>
              </div>

              {/* BOTÃO ENVIAR */}
              <div className="pt-10 pb-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-black py-5 rounded-2xl shadow-[0_0_25px_rgba(234,88,12,0.6)] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-4 group text-lg tracking-wider border-2 border-orange-400"
                >
                  {isSubmitting ? "ENVIANDO..." : "ENVIAR FORMULÁRIO"}
                  {!isSubmitting && (
                    <div className="group-hover:translate-x-2 transition-transform">
                      <IconArrowRight />
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // TELA DE SUCESSO
            <div className="flex flex-col items-center justify-center text-center py-12">
              <div className="relative mb-10">
                <div className="absolute -inset-4 bg-green-500/20 rounded-full blur-xl animate-pulse"></div>
                <div className="relative w-28 h-28 bg-green-500/10 rounded-full flex items-center justify-center border-2 border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.4)]">
                  <IconCheck />
                </div>
              </div>

              <h2 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter">
                DADOS RECEBIDOS!
              </h2>
              <p className="text-slate-400 mb-10 max-w-sm text-lg leading-relaxed">
                Excelente! A equipe da <strong>Onfire</strong> analisará suas
                informações e iniciará o planejamento das suas campanhas.
              </p>

              <button
                onClick={() => window.location.reload()}
                className="mt-12 text-slate-600 hover:text-orange-500 text-[11px] font-black uppercase tracking-[0.3em] transition-all underline underline-offset-8"
              >
                Preencher novamente
              </button>
            </div>
          )}

          <footer className="mt-16 pt-8 border-t border-slate-800/50 flex flex-col items-center">
            <p className="text-slate-600 text-[10px] font-bold uppercase tracking-[0.4em] text-center">
              Onfire Assessoria © {new Date().getFullYear()}
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
